import { EvidenceSubmission, SubmitterOutcome } from '@/backend/models/evidenceSubmission';
import { RequirementEvidenceCategory } from '@/backend/models/requirementEvidenceCategory';
import { RequirementCategoryType } from '@/backend/access/requirementEvidenceCategory/types';

export interface ReviewProgress {
  totalReviewable: number;
  reviewedCount: number;
  awaitingSubmissionCount: number;
  hasPendingReview: boolean;
  isAwaitingSubmission: boolean;
  isAllReviewed: boolean;
}

/**
 * Design submissions never go through the supplier submit step, so every design
 * submission is reviewable; evidence submissions are reviewable once submitted.
 */
export function reviewableSubmissions(
  category: RequirementEvidenceCategory,
  submissions: EvidenceSubmission[]
): EvidenceSubmission[] {
  if (category.type === RequirementCategoryType.EVIDENCE) {
    return submissions.filter((s) => s.submitterOutcome !== SubmitterOutcome.PENDING);
  }
  return submissions;
}

export function computeReviewProgress(
  categories: RequirementEvidenceCategory[],
  submissionsByCategory: Record<string, EvidenceSubmission[]>
): ReviewProgress {
  const reviewable = categories.flatMap((c) => reviewableSubmissions(c, submissionsByCategory[c.id] ?? []));
  const reviewedCount = reviewable.filter((s) => !!s.reviewerOutcome).length;
  const awaitingSubmissionCount = categories.filter(
    (c) => reviewableSubmissions(c, submissionsByCategory[c.id] ?? []).length === 0
  ).length;

  return {
    totalReviewable: reviewable.length,
    reviewedCount,
    awaitingSubmissionCount,
    hasPendingReview: reviewable.some((s) => !s.reviewerOutcome),
    isAwaitingSubmission: awaitingSubmissionCount > 0,
    isAllReviewed: reviewable.every((s) => !!s.reviewerOutcome),
  };
}
